import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import {
  GED_EXAM_SUBJECTS,
  formatUsd,
} from '../../data/studyPlans';
import {
  categoryLabel,
  createPaymentRecord,
  filterAndSortPayments,
  filterPaymentsByDateRange,
  formatPaymentDate,
  modeLabel,
  paymentDateMs,
  subscribeAllPayments,
} from '../../utils/payments';
import {
  downloadExcelCsv,
  endOfDay,
  startOfMonth,
  startOfWeek,
} from '../../utils/exportExcel';
import { printElementAsPdf } from '../../utils/exportPdf';
import PaymentReceipt from '../payments/PaymentReceipt';

const CATEGORIES = [
  { id: 'course', label: 'Course / Admission' },
  { id: 'exam', label: 'GED Exam Fee' },
  { id: 'degree', label: 'Degree / Diploma' },
  { id: 'extension', label: 'Plan Extension' },
  { id: 'manual', label: 'Manual / Other' },
];

const MODES = [
  { id: 'hosted-bank', label: 'Bank hosted checkout' },
  { id: 'online', label: 'Online banking' },
  { id: 'cash', label: 'Cash' },
];

function emptyForm() {
  return {
    studentQuery: '',
    userId: '',
    studentName: '',
    email: '',
    registrationNumber: '',
    paymentDate: new Date().toISOString().slice(0, 10),
    amount: '',
    mode: 'hosted-bank',
    category: 'manual',
    label: '',
    transactionId: '',
    subjects: [],
  };
}

function toInputDate(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function AdminPaymentsPanel() {
  const { currentUser } = useAuth();
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [lookupMsg, setLookupMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('success');
  const [viewPayment, setViewPayment] = useState(null);

  useEffect(() => subscribeAllPayments(setPayments), []);

  const filtered = useMemo(() => {
    let list = payments;
    if (categoryFilter) list = list.filter((p) => p.category === categoryFilter);
    if (modeFilter) list = list.filter((p) => p.mode === modeFilter);

    let fromMs = null;
    let toMs = null;
    const now = new Date();
    if (datePreset === 'week') {
      fromMs = startOfWeek(now).getTime();
      toMs = endOfDay(now).getTime();
    } else if (datePreset === 'month') {
      fromMs = startOfMonth(now).getTime();
      toMs = endOfDay(now).getTime();
    } else if (datePreset === 'custom') {
      if (customFrom) fromMs = new Date(`${customFrom}T00:00:00`).getTime();
      if (customTo) toMs = endOfDay(new Date(`${customTo}T00:00:00`)).getTime();
    }

    list = filterPaymentsByDateRange(list, fromMs, toMs);
    return filterAndSortPayments(list, search);
  }, [payments, search, categoryFilter, modeFilter, datePreset, customFrom, customTo]);

  async function lookupStudent() {
    setLookupMsg('');
    const qText = form.studentQuery.trim().toLowerCase();
    if (!qText) {
      setLookupMsg('Enter a student email or registration number to look up.');
      return;
    }
    try {
      let docs = [];
      const emailSnap = await getDocs(
        query(collection(db, 'users'), where('emailLower', '==', qText), limit(5))
      );
      docs = emailSnap.docs;
      if (!docs.length) {
        const snap2 = await getDocs(
          query(collection(db, 'users'), where('email', '==', form.studentQuery.trim()), limit(5))
        );
        docs = snap2.docs;
      }
      if (!docs.length) {
        const regSnap = await getDocs(
          query(
            collection(db, 'users'),
            where('registrationNumber', '==', form.studentQuery.trim().toUpperCase()),
            limit(5)
          )
        );
        docs = regSnap.docs;
      }
      if (!docs.length) {
        setLookupMsg('No student found with that email or registration number.');
        return;
      }
      const u = docs[0].data();
      setForm((p) => ({
        ...p,
        userId: docs[0].id,
        studentName: u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
        email: u.emailLower || u.email || qText,
        registrationNumber: u.registrationNumber || '',
      }));
      setLookupMsg('Account linked. Name and email are not shown or saved.');
    } catch (err) {
      console.error(err);
      setLookupMsg(err?.message || 'Lookup failed. Admins need read access to users.');
    }
  }

  function toggleSubject(id) {
    setForm((p) => ({
      ...p,
      subjects: p.subjects.includes(id)
        ? p.subjects.filter((x) => x !== id)
        : [...p.subjects, id],
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage('');
    if (!form.userId) {
      setMessageTone('error');
      setMessage('Look up and select a student first.');
      return;
    }
    if (!form.label.trim()) {
      setMessageTone('error');
      setMessage('Enter a payment label / description.');
      return;
    }
    if (!(Number(form.amount) > 0)) {
      setMessageTone('error');
      setMessage('Enter a valid amount.');
      return;
    }
    if (form.mode === 'online' && !form.transactionId.trim()) {
      setMessageTone('error');
      setMessage('Bank reference is required for online banking payments.');
      return;
    }
    if (/^\d{12,19}$/.test(form.transactionId.replace(/\s/g, ''))) {
      setMessageTone('error');
      setMessage('Do not enter a card number. Use the bank’s payment reference only.');
      return;
    }

    setSaving(true);
    try {
      const payment = await createPaymentRecord({
        userId: form.userId,
        category: form.category,
        label: form.label.trim(),
        amount: Number(form.amount),
        breakdown: {
          items: [{ name: form.label.trim(), amount: Number(form.amount) }],
          serviceAndTax: 0,
          subtotal: Number(form.amount),
        },
        mode: form.mode,
        status: 'paid',
        bankReference: form.transactionId,
        subjects: form.category === 'exam' ? form.subjects : [],
        paymentDate: form.paymentDate ? new Date(form.paymentDate) : new Date(),
        createdBy: 'admin',
        createdByUid: currentUser?.uid || '',
      });
      setMessageTone('success');
      setMessage(`Payment recorded. Reference ${payment.referenceNumber}.`);
      setViewPayment(payment);
      setForm(emptyForm());
      setLookupMsg('');
    } catch (err) {
      console.error(err);
      setMessageTone('error');
      setMessage(err?.message || 'Failed to save payment.');
    } finally {
      setSaving(false);
    }
  }

  function handleExport() {
    const headers = [
      'Payment Date',
      'Reference No',
      'Category',
      'Label',
      'Amount (USD)',
      'Mode',
      'Status',
    ];
    const rows = filtered.map((p) => [
      formatPaymentDate(p),
      p.referenceNumber || '',
      categoryLabel(p.category),
      p.label || '',
      Number(p.amount) || 0,
      modeLabel(p.mode),
      p.status || 'paid',
    ]);
    downloadExcelCsv(`SBA-payments-${datePreset}-${toInputDate(new Date())}.csv`, headers, rows);
  }

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Student payments</h2>
          <p style={s.sub}>
            Order reference, amount, and status only. Customer and card details are not shown.
          </p>
        </div>
      </div>

      {message ? <div style={s.banner(messageTone)}>{message}</div> : null}

      {viewPayment ? (
        <div style={s.invoiceBox}>
          <div style={s.invoiceHead}>
            <h3 style={s.cardTitle}>Payment receipt</h3>
            <button type="button" onClick={() => setViewPayment(null)} style={s.secondaryBtn}>
              Close
            </button>
          </div>
          <PaymentReceipt payment={viewPayment} printId="admin-payment-receipt" />
        </div>
      ) : null}

      <form onSubmit={handleSubmit} style={s.card}>
        <h3 style={s.cardTitle}>Add payment details</h3>
        <div style={s.grid}>
          <label style={{ ...s.label, gridColumn: '1 / -1' }}>
            Student email or registration no
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                style={{ ...s.input, flex: 1, minWidth: 200 }}
                value={form.studentQuery}
                onChange={(e) => setForm((p) => ({ ...p, studentQuery: e.target.value }))}
                placeholder="student@email.com or SBA-20260729-001"
              />
              <button type="button" onClick={lookupStudent} style={s.secondaryBtn}>
                Look up
              </button>
            </div>
            {lookupMsg ? <small style={{ color: '#64748b', marginTop: 4 }}>{lookupMsg}</small> : null}
          </label>

          <p style={{ ...s.label, gridColumn: '1 / -1', margin: 0 }}>
            Lookup selects the account id only. Name, email, and card details are not saved on the payment.
          </p>
          <label style={s.label}>
            Payment date
            <input
              type="date"
              style={s.input}
              value={form.paymentDate}
              onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
              required
            />
          </label>
          <label style={s.label}>
            Amount (USD)
            <input
              type="number"
              min={0}
              step="0.01"
              style={s.input}
              value={form.amount}
              onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
              required
            />
          </label>
          <label style={s.label}>
            Mode of payment
            <select
              style={s.input}
              value={form.mode}
              onChange={(e) => setForm((p) => ({ ...p, mode: e.target.value }))}
            >
              {MODES.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label style={s.label}>
            Category
            <select
              style={s.input}
              value={form.category}
              onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ ...s.label, gridColumn: '1 / -1' }}>
            Label / description
            <input
              style={s.input}
              value={form.label}
              onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
              placeholder="e.g. Exam fee — Math & Science (cash)"
              required
            />
          </label>
          {form.mode === 'online' || form.mode === 'hosted-bank' ? (
            <label style={s.label}>
              Bank reference {form.mode === 'online' ? '(required)' : '(optional)'}
              <input
                style={s.input}
                value={form.transactionId}
                onChange={(e) => setForm((p) => ({ ...p, transactionId: e.target.value }))}
              />
            </label>
          ) : null}
        </div>

        {form.category === 'exam' ? (
          <div style={{ marginTop: 14 }}>
            <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#475569' }}>
              Subjects (optional)
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {GED_EXAM_SUBJECTS.map((subj) => {
                const on = form.subjects.includes(subj.id);
                return (
                  <button
                    key={subj.id}
                    type="button"
                    onClick={() => toggleSubject(subj.id)}
                    style={{
                      ...s.chip,
                      background: on ? '#1b365d' : '#fff',
                      color: on ? '#fff' : '#1b365d',
                    }}
                  >
                    {subj.name}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <button type="submit" disabled={saving} style={s.saveBtn(saving)}>
          {saving ? 'Saving…' : 'Save payment record'}
        </button>
      </form>

      <div style={s.card}>
        <div style={s.tableHead}>
          <h3 style={{ ...s.cardTitle, margin: 0 }}>All payments ({filtered.length})</h3>
          <button type="button" onClick={handleExport} style={s.exportBtn} disabled={!filtered.length}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>download</span>
            Download Excel
          </button>
          <button
            type="button"
            onClick={() => printElementAsPdf('admin-payments-table', 'Payments Report')}
            style={s.exportBtn}
            disabled={!filtered.length}
          >
            Download PDF
          </button>
        </div>

        <div style={s.filterBar}>
          <select
            style={{ ...s.input, width: 'auto' }}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            style={{ ...s.input, width: 'auto' }}
            value={modeFilter}
            onChange={(e) => setModeFilter(e.target.value)}
          >
            <option value="">All modes</option>
            {MODES.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          <select
            style={{ ...s.input, width: 'auto' }}
            value={datePreset}
            onChange={(e) => {
              const v = e.target.value;
              setDatePreset(v);
              if (v === 'custom' && !customFrom) {
                setCustomFrom(toInputDate(startOfMonth(new Date())));
                setCustomTo(toInputDate(new Date()));
              }
            }}
          >
            <option value="all">All dates</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="custom">Custom range</option>
          </select>
          {datePreset === 'custom' ? (
            <>
              <input
                type="date"
                style={{ ...s.input, width: 'auto' }}
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <input
                type="date"
                style={{ ...s.input, width: 'auto' }}
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </>
          ) : null}
          <input
            style={{ ...s.input, minWidth: 220, flex: 1 }}
            placeholder="Search name, email, ref, reg no…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={{ overflowX: 'auto' }} id="admin-payments-table">
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Date</th>
                <th style={s.th}>Reference</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Label</th>
                <th style={s.th}>Amount</th>
                <th style={s.th}>Mode</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={s.td}>
                    No payments found for this filter. Enroll a student with payment details or add a
                    manual payment above.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr key={p.id}>
                    <td style={s.td}>{formatPaymentDate(p)}</td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: 700 }}>
                      {p.referenceNumber}
                    </td>
                    <td style={s.td}>{categoryLabel(p.category)}</td>
                    <td style={s.td}>{p.label}</td>
                    <td style={s.td}>{formatUsd(p.amount)}</td>
                    <td style={s.td}>{modeLabel(p.mode)}</td>
                    <td style={s.td}>
                      <button
                        type="button"
                        style={s.linkBtn}
                        onClick={() =>
                          setViewPayment({
                            ...p,
                            paymentDate: paymentDateMs(p)
                              ? new Date(paymentDateMs(p))
                              : p.paymentDate,
                          })
                        }
                      >
                        Receipt
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const s = {
  wrap: { flex: 1, minHeight: 0, padding: '24px 28px', overflowY: 'auto' },
  header: { marginBottom: 20 },
  title: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 18,
    fontWeight: 800,
    color: '#1b365d',
    marginBottom: 6,
  },
  sub: { fontSize: 13, color: '#64748b', lineHeight: 1.6, maxWidth: 780, margin: 0 },
  banner: (tone) => ({
    marginBottom: 16,
    padding: '12px 16px',
    borderRadius: 12,
    fontSize: 13,
    fontWeight: 600,
    ...(tone === 'error'
      ? { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }
      : { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }),
  }),
  card: {
    marginBottom: 16,
    padding: 20,
    borderRadius: 16,
    border: '1px solid #e2e8f0',
    background: '#fff',
  },
  invoiceBox: {
    marginBottom: 16,
    padding: 16,
    borderRadius: 14,
    background: '#fff',
    border: '1px solid #e2e8f0',
  },
  invoiceHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  cardTitle: { margin: '0 0 14px', fontSize: 16, fontWeight: 800, color: '#1b365d' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12,
  },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 700, color: '#475569' },
  input: {
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    fontWeight: 500,
    color: '#0f172a',
  },
  secondaryBtn: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#1b365d',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  exportBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 14px',
    borderRadius: 10,
    border: 'none',
    background: '#1b365d',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  },
  linkBtn: {
    padding: '6px 10px',
    borderRadius: 8,
    border: '1.5px solid #e2e8f0',
    background: '#f8fafc',
    color: '#1b365d',
    fontSize: 11,
    fontWeight: 700,
    cursor: 'pointer',
  },
  chip: {
    padding: '8px 12px',
    borderRadius: 999,
    border: '1.5px solid #e2e8f0',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  saveBtn: (disabled) => ({
    marginTop: 16,
    padding: '12px 22px',
    borderRadius: 12,
    border: 'none',
    background: disabled ? '#94a3b8' : '#1b365d',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  tableHead: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 12,
  },
  filterBar: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 14,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '10px 8px',
    borderBottom: '2px solid #e2e8f0',
    color: '#64748b',
    fontSize: 11,
    textTransform: 'uppercase',
  },
  td: { padding: '10px 8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
};
